import React from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { formatNum, formatSgd } from "../../lib/utils";
import { ProvenanceTrigger } from "../provenance/ProvenanceTrigger";
import { X, Plus, ArrowUp, ArrowDown, Minus, Leaf, Users, ShieldCheck, LayoutGrid } from "lucide-react";

const PILLAR = {
  Environment: { dot: "#00E65B", Icon: Leaf },
  Social: { dot: "#38BDF8", Icon: Users },
  Governance: { dot: "#FFB800", Icon: ShieldCheck },
  Overview: { dot: "#A78BFA", Icon: LayoutGrid },
};
const TONE = {
  lime: "border-accent/40 bg-accent/5", success: "border-emerald-500/40 bg-emerald-500/5",
  warning: "border-amber-500/40 bg-amber-500/5", danger: "border-red-500/40 bg-red-500/5", default: "border-border",
};

export const widgetSpan = (render) => (["trend", "bars"].includes(render) ? "lg:col-span-2" : "");

const DirBadge = ({ dir }) => {
  if (!dir) return null;
  const map = { improving: ["text-[#00E65B]", ArrowDown], worsening: ["text-red-500", ArrowUp], flat: ["text-muted-foreground", Minus] };
  const [tone, Icon] = map[dir] || map.flat;
  return <span className={`inline-flex items-center gap-0.5 text-[10px] ${tone}`}><Icon className="h-3 w-3" /> {dir}</span>;
};

function Body({ w }) {
  const d = w.data || {};
  switch (w.render) {
    case "stat":
      return (
        <div className="font-display font-bold text-3xl tracking-tight mt-2">
          {formatNum(d.value, { decimals: 1 })}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">{d.unit}</span>
        </div>
      );
    case "kpi":
      return (
        <div className="mt-2">
          {d.error ? <div className="text-xs text-red-400 font-mono">⚠ {d.error}</div> : (
            <div className="font-display font-bold text-3xl tracking-tight">
              {formatNum(d.value, { decimals: 2 })}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">{d.unit}</span>
            </div>
          )}
          {(d.progress_pct != null || d.target != null) && (
            <div className="mt-3">
              {d.progress_pct != null && <Progress value={Math.max(0, Math.min(100, d.progress_pct))} className="h-1.5" />}
              <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono text-muted-foreground">
                <span>{d.target != null ? `Target ${formatNum(d.target, { decimals: 1 })} ${d.unit}` : ""}</span>
                <DirBadge dir={d.direction} />
              </div>
            </div>
          )}
        </div>
      );
    case "trend":
      return (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={d.series} margin={{ top: 8, right: 0, left: -20, bottom: 0 }}>
            <defs>
              {(d.keys || []).map((k) => (
                <linearGradient key={k} id={`g-${w.key}-${k}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={(d.colors || {})[k] || "#00E65B"} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={(d.colors || {})[k] || "#00E65B"} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} />
            <Tooltip />
            {(d.keys || []).map((k) => (
              <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={(d.colors || {})[k] || "#00E65B"}
                fill={`url(#g-${w.key}-${k})`} strokeWidth={2} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );
    case "donut":
      return (
        <>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={d.slices} dataKey="value" innerRadius={42} outerRadius={68} paddingAngle={2}>
                {(d.slices || []).map((s, i) => <Cell key={i} fill={s.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {(d.slices || []).map((s) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />{s.name}</span>
                <span className="font-mono">{formatNum(s.value, { decimals: 1 })}</span>
              </div>
            ))}
          </div>
        </>
      );
    case "progress":
      return (
        <div className="mt-1">
          <div className="font-display font-bold text-3xl" title={d.method}>{d.headline}<span className="text-sm text-muted-foreground"> {d.headline_unit}</span></div>
          {d.scope_label && <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="dq-scope-label">{d.scope_label}</div>}
          <div className="space-y-2.5 mt-4">
            {(d.rows || []).map((r) => (
              <div key={r.label}>
                <div className="flex justify-between text-[11px] font-mono mb-1"><span className="text-muted-foreground">{r.label}</span><span>{r.pct}%</span></div>
                <Progress value={r.pct} className="h-1.5" />
              </div>
            ))}
          </div>
        </div>
      );
    case "bars":
      return (
        <div className="space-y-2.5 mt-2">
          {(d.rows || []).length === 0 && <div className="text-sm text-muted-foreground mt-1">No tracked requirements yet — set them up in Reporting & Compliance.</div>}
          {(d.rows || []).map((f) => (
            <div key={f.framework} className="flex items-center gap-3 text-sm">
              <div className="w-36 truncate">{f.framework}</div>
              <Progress value={f.coverage_pct} className="flex-1 h-1.5" />
              <span className="font-mono text-xs w-20 text-right" title={f.covered != null ? `${f.covered} of ${f.applicable} requirements complete/ready/published` : undefined}>
                {f.covered != null ? `${f.covered}/${f.applicable} · ` : ""}{f.coverage_pct}%
              </span>
            </div>
          ))}
        </div>
      );
    case "targets":
      return (
        <div className="space-y-4 mt-2">
          {(d.targets || []).length === 0 && <div className="text-sm text-muted-foreground">No targets set.</div>}
          {(d.targets || []).map((t) => {
            const pct = t.financing_contribution_tonnes > 0 ? t.progress_pct_with_financing : t.progress_pct;
            return (
              <div key={t._id || t.name}>
                <div className="flex items-center justify-between mb-1"><span className="text-sm font-medium">{t.name}</span><span className="font-mono text-xs" title="Progress achieved toward this target to date">{pct}% of target achieved</span></div>
                <Progress value={pct} className="h-1.5" />
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">{t.baseline_year} → {t.target_year}</div>
              </div>
            );
          })}
        </div>
      );
    case "carbon_tax":
      return (
        <div className="mt-1">
          {d.is_taxable ? (
            <>
              <div className="font-display text-3xl font-bold">{formatSgd(d.current_liability_sgd)}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">Statutory CPA · Scope 1 only · S${d.rate_current}/tCO2e</div>
              <div className="flex justify-between mt-3 text-sm"><span className="text-muted-foreground">Projected 2026 (statutory)</span><span className="font-mono text-amber-400">{formatSgd(d.projected_2026_liability_sgd)}</span></div>
            </>
          ) : (
            <>
              <div className="font-display text-2xl font-bold text-muted-foreground">Not Taxable</div>
              <div className="text-xs text-muted-foreground mt-1">Statutory CPA — Scope 1 below the 25k tCO2e/yr threshold.</div>
            </>
          )}
          {d.internal_price && (
            <div className="mt-3 pt-2 border-t border-border" data-testid="carbon-tax-internal">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Internal price (Scope 1+2)</span><span className="font-mono">{formatSgd(d.internal_price.current_sgd)}</span></div>
              <div className="flex justify-between text-xs mt-1"><span className="text-muted-foreground">2026 at S${d.rate_2026}/tCO2e</span><span className="font-mono text-muted-foreground">{formatSgd(d.internal_price.projected_2026_sgd)}</span></div>
            </div>
          )}
        </div>
      );
    case "green_financing":
      return d && d.active_projects > 0 ? (
        <div className="mt-1">
          <div className="font-display text-3xl font-bold">{formatNum(d.co2_avoided_tonnes, { decimals: 1 })}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">tCO2e/yr</span></div>
          <div className="text-xs text-muted-foreground mt-1 font-mono">≈ {d.contribution_pct}% of gross emissions</div>
          <div className="flex justify-between mt-4 text-sm"><span className="text-muted-foreground">Annual savings</span><span className="font-mono text-accent">{formatSgd(d.annual_savings_sgd)}</span></div>
        </div>
      ) : d && d.total_cases > 0 ? (
        <div className="mt-1" data-testid="gf-widget-cases-fallback">
          <div className="font-display text-3xl font-bold">{d.total_cases}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">case{d.total_cases === 1 ? "" : "s"}</span></div>
          <div className="text-xs text-muted-foreground mt-1 font-mono">{formatSgd(d.requested_quantum_sgd || 0)} requested</div>
          <div className="flex justify-between mt-4 text-sm"><span className="text-muted-foreground">Financed impact</span><span className="font-mono text-muted-foreground">Pending simulation</span></div>
        </div>
      ) : (
        <div className="mt-2"><div className="font-display text-xl font-bold text-muted-foreground">No financed impact yet</div><div className="text-xs text-muted-foreground mt-2">Prepare a financing case to see contribution here.</div></div>
      );
    default:
      return <div className="text-sm text-muted-foreground mt-2">Unsupported widget.</div>;
  }
}

export function WidgetCard({ w, onRemove, onAdd, compact, footer, provYear }) {
  const p = PILLAR[w.pillar] || PILLAR.Overview;
  const tone = TONE[w.data?.tone] || "border-border";
  return (
    <div className={`card-flat p-5 relative group flex flex-col ${tone}`} data-testid={`widget-${w.key}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.dot }} />
          <div className="label-eyebrow truncate">{w.title}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
        {!onAdd && <ProvenanceTrigger pkey={w.key} year={provYear} />}
        {onRemove && (
          <button onClick={() => onRemove(w.key)} data-testid={`remove-widget-${w.key}`}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400 shrink-0" title="Remove from dashboard">
            <X className="h-4 w-4" />
          </button>
        )}
        {onAdd && (
          <button onClick={() => onAdd(w.key)} data-testid={`add-widget-${w.key}`}
            className="text-accent hover:text-accent/80 shrink-0" title="Add to dashboard">
            <Plus className="h-4 w-4" />
          </button>
        )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-1">
        <Badge variant="outline" className="text-[9px] py-0">{w.pillar}</Badge>
        {w.category && <span className="text-[10px] text-muted-foreground truncate">{w.category}</span>}
      </div>
      {!compact && <div className="flex-1"><Body w={w} /></div>}
      {footer && <div className="mt-4 pt-3 border-t border-border flex justify-end">{footer}</div>}
    </div>
  );
}
